import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import ViewInstantWinWinnerPageComponent from "./viewInstantWinWinnerPage.component";
import { hideBackdrop, showBackdrop } from "modules/backdrop";
import { addAlert } from "modules/notification";
import { getApiLang } from "app/feature/constants";
import moment from "moment";
import { ViewWinnerContext } from "../../contexts/ViewWinnerContext";
import {
  // Actions
  fetchClaimList,
  fetchUnclaimList,
  fetchLuckyDrawDetails,
  fetchClaimSummary,
  // Slice actions
  setActiveTab,
  setFilter,
  clearFilters,
  applyFilters,
  selectRow,
  selectAllRows,
  clearSelectedRows,
  setPage,
  setRowsPerPage,
  openAdminDialog,
  closeAdminDialog,
  openBulkProcessDialog,
  closeBulkProcessDialog,
  setIsExporting,
  resetState,
} from "../../redux";
import luckyDrawV2Api from "app/api/luckyDrawV2";

export default function ViewInstantWinWinnerPageContainer({
  isClaimListView = false,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { uuid } = useParams();

  // Redux selectors
  const accessToken = useSelector((state) => state.session.accessToken);
  const lang = useSelector((state) => state.constant.languages);
  const companyId = useSelector((state) => state.profile.companyID);
  const timezone = useSelector((state) => state.profile.timezone);

  // View Claim List state from Redux
  const {
    selectedLuckyDraw,
    activeTab,
    filters,
    appliedFilters,
    winners,
    totalCount,
    metrics,
    selectedRows,
    page,
    rowsPerPage,
    isFetching,
    isExporting,
    isUpdating,
    adminDialog,
    bulkProcessDialog,
  } = useSelector((state) => state.viewClaimList);

  // Fetch lucky draw details and metrics on mount
  useEffect(() => {
    if (uuid) {
      dispatch(fetchLuckyDrawDetails(uuid));
      dispatch(fetchClaimSummary(uuid));
    }
    // Reset state when unmounting
    return () => {
      dispatch(resetState());
    };
  }, [uuid, dispatch]);

  // Fetch data when activeTab, page, rowsPerPage, or appliedFilters changes
  useEffect(() => {
    if (uuid) {
      fetchPrizes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, activeTab, page, rowsPerPage, appliedFilters]);

  // Fetch prizes based on active tab
  const fetchPrizes = useCallback(() => {
    const baseParams = {
      luckyDrawUuid: uuid,
      limit: rowsPerPage,
      skip: page * rowsPerPage,
      searchText: appliedFilters.searchText || undefined,
      searchType: appliedFilters.searchType || undefined,
      prizeType: appliedFilters.prizeType || undefined,
    };

    if (activeTab === 0) {
      // TO PROCESS tab
      let statusValue = undefined;
      if (appliedFilters.statuses && appliedFilters.statuses !== "") {
        if (appliedFilters.statuses === "Processed") {
          statusValue = "processed";
        } else if (appliedFilters.statuses === "Rejected") {
          statusValue = "rejected";
        } else {
          statusValue = "unprocessed";
        }
      }
      dispatch(
        fetchClaimList({
          ...baseParams,
          status: statusValue,
          claimedDateStart: appliedFilters.dateStart
            ? moment(appliedFilters.dateStart).format("YYYY-MM-DD")
            : undefined,
          claimedDateEnd: appliedFilters.dateEnd
            ? moment(appliedFilters.dateEnd).format("YYYY-MM-DD")
            : undefined,
        })
      );
    } else if (activeTab === 1) {
      // UNCLAIMED PRIZE tab
      dispatch(
        fetchUnclaimList({
          ...baseParams,
          wonDateStart: appliedFilters.dateStart
            ? moment(appliedFilters.dateStart).format("YYYY-MM-DD")
            : undefined,
          wonDateEnd: appliedFilters.dateEnd
            ? moment(appliedFilters.dateEnd).format("YYYY-MM-DD")
            : undefined,
        })
      );
    }
  }, [uuid, activeTab, page, rowsPerPage, appliedFilters, dispatch]);

  // Get status label for a winner
  const getStatusLabel = (winner) => {
    if (winner.claim_fulfill_status === 1 || winner.consumer_claim_status === "processed") {
      return "Processed";
    } else if (winner.claim_fulfill_status === 2 || winner.consumer_claim_status === "rejected") {
      return "Rejected";
    } else {
      return "Unprocessed";
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    dispatch(setActiveTab(newValue));
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    dispatch(setFilter({ field, value }));
  };

  // Handle clear filters
  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  // Handle search (apply filters)
  const handleSearch = () => {
    dispatch(applyFilters());
  };

  // Handle row selection
  const handleSelectRow = (winnerUuid) => {
    dispatch(selectRow(winnerUuid));
  };

  // Handle select all rows
  const handleSelectAllRows = (checked) => {
    if (checked) {
      const allUuids = winners
        .slice(0, rowsPerPage)
        .map((w) => w.uuid);
      dispatch(selectAllRows({ checked, uuids: allUuids }));
    } else {
      dispatch(selectAllRows({ checked, uuids: [] }));
    }
  };

  // Handle batch process - Open confirmation dialog
  const handleBatchProcess = () => {
    if (selectedRows.length === 0) {
      dispatch(
        addAlert({
          severity: "warning",
          message: "Please select at least one winner to process",
        })
      );
      return;
    }

    const selectedWinners = winners.filter((w) =>
      selectedRows.includes(w.uuid)
    );
    dispatch(openBulkProcessDialog(selectedWinners));
  };

  // Handle close bulk process dialog
  const handleCloseBulkProcessDialog = () => {
    dispatch(closeBulkProcessDialog());
  };

  // Handle confirm bulk process
  const handleConfirmBulkProcess = async (status, notes) => {
    // Get winner UUIDs from the bulk process dialog (more reliable than selectedRows)
    const winnerUuidsToProcess = bulkProcessDialog.winners.map((w) => w.uuid);

    if (winnerUuidsToProcess.length === 0) {
      dispatch(
        addAlert({
          severity: "warning",
          message: "No winners selected to process",
        })
      );
      return;
    }

    dispatch(showBackdrop());

    try {
      // Call updateClaimStatus API (auto sends email for processed/rejected)
      await luckyDrawV2Api.updateClaimStatus({
        luckyDrawUuid: uuid,
        winnerUuids: winnerUuidsToProcess,
        status: status,
        adminNotes: notes,
      });

      dispatch(
        addAlert({
          severity: "success",
          message: `Successfully ${
            status === 1 ? "processed" : status === 2 ? "rejected" : "marked as unprocessed"
          } ${winnerUuidsToProcess.length} winner(s)`,
        })
      );

      // Refresh data and metrics
      fetchPrizes();
      dispatch(fetchClaimSummary(uuid));
      dispatch(clearSelectedRows());
      dispatch(closeBulkProcessDialog());
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message: error?.code
            ? getApiLang(lang, error.code)
            : error?.message || "Failed to update winner status. Please try again.",
        })
      );
    } finally {
      dispatch(hideBackdrop());
    }
  };

  // Handle export
  const handleExport = () => {
    dispatch(setIsExporting(true));

    let status = undefined;
    if (activeTab === 0) {
      if (appliedFilters.statuses && appliedFilters.statuses !== "") {
        if (appliedFilters.statuses === "Processed") {
          status = "processed";
        } else if (appliedFilters.statuses === "Rejected") {
          status = "rejected";
        } else {
          status = "unprocessed";
        }
      }
    } else {
      status = "unclaimed";
    }

    luckyDrawV2Api
      .exportClaimList({
        luckyDrawUuid: uuid,
        token: accessToken,
        status: status,
        searchText: appliedFilters.searchText || undefined,
        searchType: appliedFilters.searchType || undefined,
        prizeType: appliedFilters.prizeType || undefined,
        claimedDateStart:
          activeTab === 0 && appliedFilters.dateStart
            ? moment(appliedFilters.dateStart).format("YYYY-MM-DD")
            : undefined,
        claimedDateEnd:
          activeTab === 0 && appliedFilters.dateEnd
            ? moment(appliedFilters.dateEnd).format("YYYY-MM-DD")
            : undefined,
        wonDateStart:
          activeTab === 1 && appliedFilters.dateStart
            ? moment(appliedFilters.dateStart).format("YYYY-MM-DD")
            : undefined,
        wonDateEnd:
          activeTab === 1 && appliedFilters.dateEnd
            ? moment(appliedFilters.dateEnd).format("YYYY-MM-DD")
            : undefined,
        timezone: timezone || "Asia/Kuala_Lumpur",
        winnerUuids: selectedRows.length > 0 ? selectedRows : undefined,
      })
      .finally(() => {
        dispatch(setIsExporting(false));
      });
  };

  // Handle open admin dialog
  const handleOpenAdminDialog = (winner) => {
    dispatch(openAdminDialog(winner));
  };

  // Handle close admin dialog
  const handleCloseAdminDialog = () => {
    dispatch(closeAdminDialog());
  };

  // Handle save admin action
  const handleSaveAdminAction = async (winnerUuid, status, notes) => {
    try {
      if (activeTab === 1) {
        // Unclaimed Prize tab - only update admin notes
        await luckyDrawV2Api.updateAdminNotes({
          luckyDrawUuid: uuid,
          winnerUuid: winnerUuid,
          adminNotes: notes,
        });
      } else {
        // To Process tab - update status and admin notes (auto sends email)
        await luckyDrawV2Api.updateClaimStatus({
          luckyDrawUuid: uuid,
          winnerUuid: winnerUuid,
          status: status,
          adminNotes: notes,
        });
      }

      dispatch(
        addAlert({
          severity: "success",
          message: "Winner information updated successfully",
        })
      );

      // Refresh data and metrics
      fetchPrizes();
      dispatch(fetchClaimSummary(uuid));
      dispatch(closeAdminDialog());
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message: error?.code
            ? getApiLang(lang, error.code)
            : error?.message || "Failed to update winner status. Please try again.",
        })
      );
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
  };

  // Handle back navigation
  const handleBack = () => {
    history.goBack();
  };

  const contextValue = {
    companyId,
    lang,
    timezone,
    selectedLuckyDraw,
    activeTab,
    filters,
    selectedRows,
    flattenedWinners: winners,
    filteredWinners: winners,
    metrics,
    adminDialog,
    bulkProcessDialog,
    isFetching,
    isExporting,
    isUpdating,
    page,
    rowsPerPage,
    totalCount,
    isClaimListView,
    handleTabChange,
    handleFilterChange,
    handleClearFilters,
    handleSearch,
    handleSelectRow,
    handleSelectAllRows,
    handleBatchProcess,
    handleCloseBulkProcessDialog,
    handleConfirmBulkProcess,
    handleExport,
    handleOpenAdminDialog,
    handleCloseAdminDialog,
    handleSaveAdminAction,
    handleChangePage,
    handleChangeRowsPerPage,
    handleBack,
    getStatusLabel,
  };

  return (
    <ViewWinnerContext.Provider value={contextValue}>
      <ViewInstantWinWinnerPageComponent />
    </ViewWinnerContext.Provider>
  );
}
